import React, { ReactNode, useCallback, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  FlatList,
} from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { CancelConfirmTaskAndProjectModal } from '@components/Modals/CancelConfirmTaskAndProjectModal';
import { ModalItemType } from '@components/Modals/Modal';
import { Box, ShadowBox, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';

type FinalMobileModalProps = {
  accessibilityLabel: string;
  visible: boolean;
  children: ReactNode;
  onClose?: () => void;
  height?: number;
  width?: number;
  title?: string;
  isBottomModal?: boolean;
  showCloseIcon?: boolean;
};

export const WebV3Modal = ({
  accessibilityLabel,
  visible,
  onClose,
  children,
  height,
  width,
  title,
  isBottomModal = false,
}: Omit<FinalMobileModalProps, 'showCloseIcon'>) => {
  return (
    <Modal
      animationType='fade'
      transparent
      visible={visible}
      onRequestClose={onClose}>
      <Box
        accessibilityLabel={
          accessibilityLabel ? `${accessibilityLabel} Screen` : 'Modal'
        }
        style={
          isBottomModal ? styles.modalContainerBottom : styles.modalContainer
        }>
        {/* Backdrop */}
        <TouchableWithoutFeedback onPress={onClose}>
          <Box style={styles.backdrop} />
        </TouchableWithoutFeedback>

        <ShadowBox
          accessibilityLabel={title ? `${title} Screen` : 'Modal'}
          variant='thin'
          style={[
            styles.popUp,
            styles.centered,
            isBottomModal ? styles.bottomSheetShape : styles.centerShape,
            width !== undefined && { width },
            height !== undefined && { height },
          ]}
          overflow='hidden'>
          <SafeAreaView edges={isBottomModal ? ['bottom'] : ['top']}>
            {!!title && (
              <Box flexDirection='row' mx='l' mt='m' alignItems='center'>
                <Text
                  flex={1}
                  flexShrink={1}
                  marginRight='m'
                  numberOfLines={0}
                  ellipsizeMode='tail'
                  variant='heading2'
                  color='textPrimary'>
                  {title}
                </Text>

                <Icon
                  accessibilityLabel={title ? `Close ${title}` : 'Close'}
                  name='X'
                  variant='m'
                  color='black'
                  onPress={onClose}
                />
              </Box>
            )}

            {children}
          </SafeAreaView>
        </ShadowBox>
      </Box>
    </Modal>
  );
};

export const MobileModal = ({
  accessibilityLabel,
  visible,
  onClose,
  children,
  height,
  width,
  title,
  isBottomModal = false,
  showCloseIcon = true,
}: FinalMobileModalProps) => {
  return (
    <Modal
      animationType='fade'
      transparent
      visible={visible}
      onRequestClose={onClose}
      statusBarTranslucent
      navigationBarTranslucent>
      <Box
        accessibilityLabel={
          accessibilityLabel ? `${accessibilityLabel} Screen` : 'Modal'
        }
        style={
          isBottomModal ? styles.modalContainerBottom : styles.modalContainer
        }>
        <TouchableWithoutFeedback onPress={onClose}>
          <Box style={styles.backdrop} />
        </TouchableWithoutFeedback>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={[
            styles.keyboardAvoiding,
            isBottomModal && styles.keyboardAvoidingBottom,
          ]}>
          <ShadowBox
            accessibilityLabel={title ? `${title} Screen` : 'Modal'}
            variant='thin'
            style={[
              styles.popUp,
              styles.centered,
              isBottomModal ? styles.bottomSheetShape : styles.centerShape,
              width !== undefined && { width },
              height !== undefined && { height },
            ]}
            overflow='hidden'>
            <SafeAreaView edges={isBottomModal ? ['bottom'] : ['top']}>
              {!!title && (
                <Box flexDirection='row' mx='l' mt='m' alignItems='center'>
                  <Text
                    flex={1}
                    flexShrink={1}
                    marginRight='m'
                    numberOfLines={0}
                    ellipsizeMode='tail'
                    variant='heading2'
                    color='textPrimary'>
                    {title}
                  </Text>
                  {showCloseIcon && (
                    <Icon
                      accessibilityLabel={title ? `Close ${title}` : 'Close'}
                      name='X'
                      variant='m'
                      color='black'
                      onPress={onClose}
                    />
                  )}
                </Box>
              )}
              {children}
            </SafeAreaView>
          </ShadowBox>
        </KeyboardAvoidingView>
      </Box>
    </Modal>
  );
};

type ConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
  doNotShowCancel?: boolean;
  cancelText?: string;
  onConfirm?: () => void;
};

export function useConfirmModal() {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);

  const showConfirm = useCallback((opts: ConfirmOptions) => {
    setOptions(opts);
  }, []);

  const modal1 =
    Platform.OS === 'web' ? (
      <CancelConfirmTaskAndProjectModal
        showModal={options !== null}
        onClose={() => setOptions(null)}
        onPress={() => {
          setOptions(null);
          options?.onConfirm?.();
        }}
        isAlert={
          (options?.doNotShowCancel ?? false) ||
          options?.cancelText === undefined
        }
        buttonText={options?.confirmText ?? 'Confirm'}
        title={options?.title ?? ''}
        message={options?.message ?? ''}
      />
    ) : (
      <MobileModal
        accessibilityLabel={options?.title ?? ''}
        visible
        title={options?.title ?? ''}
        onClose={() => setOptions(null)}>
        <Box px='l' pt='xs' pb='m'>
          <Text variant='body1' color='textSecondary'>
            {options?.message ?? ''}
          </Text>

          <Box flexDirection='row' justifyContent='flex-end' mt='m'>
            <TouchableOpacity onPress={() => setOptions(null)}>
              <Text variant='buttonLabel' color='textSecondary'>
                {options?.cancelText ?? 'Cancel'}
              </Text>
            </TouchableOpacity>

            <Box width={40} />

            <TouchableOpacity
              onPress={() => {
                setOptions(null);
                options?.onConfirm?.();
              }}>
              <Text variant='buttonLabel' color='greenSecondary'>
                {options?.confirmText ?? 'Confirm'}
              </Text>
            </TouchableOpacity>
          </Box>
        </Box>
      </MobileModal>
    );
  const modal = options ? modal1 : <></>;
  return { showConfirm, modal };
}

type BottomModalOptions = {
  items?: ModalItemType[];
};

export function useBottomModal() {
  const [options, setOptions] = useState<BottomModalOptions | null>(null);

  const showBottomModal = useCallback((opts: BottomModalOptions) => {
    setOptions(opts);
  }, []);

  const close = useCallback(() => {
    setOptions(null);
  }, []);

  const translateY = useSharedValue(0);
  const CLOSE_THRESHOLD = 20;

  const pan = Gesture.Pan()
    .onUpdate((e) => {
      if (e.translationY > 0) {
        translateY.value = e.translationY;
      }
    })
    .onEnd((e) => {
      const shouldClose = e.translationY > CLOSE_THRESHOLD || e.velocityY > 800;
      if (shouldClose) {
        translateY.value = 0;
        runOnJS(close)();
      } else {
        translateY.value = withTiming(0);
      }
    });

  const sheetStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const renderItem = ({
    item,
    index,
  }: {
    item: ModalItemType;
    index: number;
  }) => (
    <TouchableOpacity
      key={String(index)}
      disabled={item.disable}
      onPress={() => {
        item.onSelect();
        close();
      }}
      accessibilityLabel={item.accessibilityLabel ?? item.title}>
      <Box
        flexDirection='row'
        paddingVertical='s'
        paddingHorizontal='xxs'
        opacity={item.disable ? 0.5 : 1}
        alignItems='center'>
        <Box flex={1} flexDirection='row'>
          <Box flex={1} flexDirection='row'>
            {item.iconName && (
              <Icon
                name={item.iconName}
                variant='l'
                marginRight='m'
                color={item.color || 'textPrimary'}
              />
            )}
            <Text variant='body' color={item.color || 'textPrimary'}>
              {item.title}
            </Text>
          </Box>

          {item.label && (
            <Text
              variant='metadata'
              color='textSecondary'
              textTransform='capitalize'>
              {item.label}
            </Text>
          )}

          {item.trailingIconName && (
            <Icon name='Check' color='greenBrand' variant='l' />
          )}
        </Box>
      </Box>
    </TouchableOpacity>
  );

  const modal = options ? (
    <MobileModal
      accessibilityLabel='Bottom modal'
      visible
      isBottomModal
      onClose={close}>
      <GestureDetector gesture={pan}>
        <Animated.View style={sheetStyle}>
          <Box p='m'>
            <TouchableOpacity onPress={close}>
              <Box
                backgroundColor='grey03'
                height={4}
                width={56}
                borderRadius='xxxs'
                alignItems='center'
                alignSelf='center'
                marginBottom='xs'
              />
            </TouchableOpacity>

            {options.items && (
              <FlatList
                data={options.items}
                scrollEnabled={false}
                keyExtractor={(_, idx) => String(idx)}
                renderItem={renderItem}
              />
            )}
          </Box>
        </Animated.View>
      </GestureDetector>
    </MobileModal>
  ) : null;

  return { showBottomModal, modal };
}

const styles = StyleSheet.create({
  modalContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainerBottom: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'black',
    opacity: 0.5,
  },
  keyboardAvoiding: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyboardAvoidingBottom: {
    justifyContent: 'flex-end',
  },
  popUp: {
    backgroundColor: 'white',
  },
  centered: {
    width: '90%',
    maxWidth: 520,
  },
  fullScreen: {
    width: '100%',
    height: '100%',
  },
  bottomSheetShape: {
    width: '100%',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  centerShape: {
    borderRadius: 16,
  },
});
